// @ts-check
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { tolerantId, fetchUsersByField } from "./firestoreHelpers";

/**
 * Personal (colaboradores) de un edificio para las pantallas de staff:
 * Asistencia, Chat, Recibos, Licencias, Turnos, Tareas y Amenities.
 *
 * - Vínculos: employeebuilding del edificio. El campo es `building` (así lo
 *   escriben functions/identity/staff.js y el cliente viejo; `buildingid` no
 *   existe en esa colección) y la consulta es 'in' tolerantId, que prueba la
 *   regla isStaffBid(resource.data.building) de las reglas 0D.
 * - Nombre y contacto: la ficha users del colaborador (userid == employeeid,
 *   typeuser 3). C10 de las reglas 0D: el staff lee fichas ajenas solo si la
 *   consulta fija accessBids array-contains el edificio. Van por
 *   fetchUsersByField("userid", variantes, edificio) de firestoreHelpers
 *   (accessBids del edificio + userid in, en tandas), que devuelve TODAS las
 *   fichas de cada userid: fetchUsersByUserIds deja una sola y prefiere la de
 *   residente, y acá hace falta la de colaborador. El servidor suma el
 *   edificio a accessBids del colaborador por su employeebuilding.
 * - La colección employee NO se lee: es un espejo de esa ficha con id de
 *   documento aleatorio, y con las reglas 0D el staff no la puede listar (list
 *   solo superadmin). Sin un id guardado en el vínculo no hay qué pedir con getDoc.
 */

/** userid / employeeid → clave canónica: 300 | "300" | "0300" → "300". */
export const claveId = (x) => {
  if (x == null) return "";
  const s = String(x).trim();
  return /^\d+$/.test(s) ? String(Number(s)) : s;
};

const textoDe = (v) => (v == null ? "" : String(v).trim());

/**
 * Nombre y apellido por separado. Las fichas viejas sin NombreCompleto tienen
 * solo `name` (nombre y apellido juntos): no se repite el apellido.
 */
const partesNombre = (u) => {
  const apellido = textoDe(u.ApellidosCompleto) || textoDe(u.apellido);
  const nombre = textoDe(u.NombreCompleto) || textoDe(u.nombre);
  if (nombre) return { nombre, apellido };
  const name = textoDe(u.name);
  if (!name || !apellido) return { nombre: name, apellido };
  if (name.endsWith(apellido)) return { nombre: name.slice(0, -apellido.length).trim(), apellido };
  return name.includes(apellido) ? { nombre: name, apellido: "" } : { nombre: name, apellido };
};

/** La ficha del colaborador: typeuser 3, vigente y sin fusionar antes que las demás. */
const rangoFicha = (u) => {
  const colaborador = Number(u.typeuser) === 3;
  const vigente = !u.archivedAt && u.mergedIntoUserid == null;
  return (colaborador ? 0 : 2) + (vigente ? 0 : 1);
};

/**
 * Fichas users por userid (mapa clave canónica → ficha elegida), con la forma
 * C10: accessBids del edificio + userid in. Los ids van en su forma canónica
 * ("0300" → "300"): una ficha guardada con otra forma no tiene accessBids
 * (el servidor no la cuenta), así que tampoco la vería el staff.
 */
async function fichasPorUserid(employeeids, buildingId) {
  const claves = [...new Set(employeeids.map(claveId).filter(Boolean))];
  const porClave = new Map();
  if (!claves.length) return porClave;
  try {
    const fichas = await fetchUsersByField("userid", claves.flatMap((k) => tolerantId(k)), buildingId);
    (fichas || []).forEach(({ id, ...data }) => {
      const u = { ...data, docId: id };
      const k = claveId(u.userid);
      const previa = porClave.get(k);
      if (!previa || rangoFicha(u) < rangoFicha(previa)) porClave.set(k, u);
    });
  } catch (err) {
    // Sin nombres la lista sigue sirviendo (las pantallas muestran un genérico).
    console.warn("personalEdificio: no se pudieron leer las fichas users:", err?.code || err?.message || err);
  }
  return porClave;
}

/**
 * Colaboradores del edificio, uno por employeeid, ordenados por nombre.
 *
 * Cada uno trae `id` (el del employeebuilding, clave estable para React),
 * `ebId`, `employeeid` (tal cual está guardado), `nombre`, `apellido`,
 * `name`, `email`, `celular`, `userDocId` y `vinculo` (el employeebuilding:
 * horario, días, sueldo e inicio de actividad).
 *
 * @param {string|number|null|undefined} buildingId
 * @returns {Promise<Array<Record<string, any>>>}
 */
export async function cargarPersonalEdificio(buildingId) {
  if (buildingId == null || buildingId === "") return [];
  const ebSnap = await getDocs(
    query(collection(db, "employeebuilding"), where("building", "in", tolerantId(buildingId)))
  );
  const vinculos = [];
  const vistos = new Set();
  ebSnap.docs.forEach((d) => {
    const eb = d.data() || {};
    const k = claveId(eb.employeeid);
    if (!k || vistos.has(k)) return;
    vistos.add(k);
    vinculos.push({ ...eb, ebId: d.id });
  });
  if (!vinculos.length) return [];

  const fichas = await fichasPorUserid(vinculos.map((eb) => eb.employeeid), buildingId);
  const personal = vinculos.map((eb) => {
    const u = fichas.get(claveId(eb.employeeid)) || {};
    const { nombre, apellido } = partesNombre(u);
    const { ebId, ...vinculo } = eb;
    return {
      id: ebId,
      ebId,
      employeeid: eb.employeeid,
      nombre,
      apellido,
      name: `${nombre} ${apellido}`.trim(),
      NombreCompleto: nombre,
      ApellidosCompleto: apellido,
      email: textoDe(u.email),
      celular: textoDe(u.celular),
      userDocId: u.docId || null,
      vinculo,
    };
  });
  personal.sort((a, b) =>
    (a.name || claveId(a.employeeid)).localeCompare(b.name || claveId(b.employeeid), "es", { sensitivity: "base" })
  );
  return personal;
}

/** Colaborador de la lista por employeeid (tolerante a número/string). */
export const buscarColaborador = (personal, employeeid) => {
  const k = claveId(employeeid);
  return k ? (personal || []).find((p) => claveId(p.employeeid) === k) || null : null;
};
